import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Building2,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  Timer,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  X,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DashboardStats } from '../types';
import apiService from '../services/api';

const STATUS_COLORS = {
  pending: '#f97316',
  inProgress: '#3b82f6',
  rework: '#ef4444',
  resolved: '#10b981',
  closed: '#6b7280',
};

const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isRTL = i18n.language === 'ar';

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getDashboardStats();
      setStats(data);
      setError('');
    } catch (err) {
      setError(isRTL ? 'فشل تحميل الإحصائيات' : 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [isRTL]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 via-green-50 to-emerald-50 flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 via-green-50 to-emerald-50 p-8">
        <div className="bg-gradient-to-r from-red-50 to-rose-50 border-r-4 border-red-500 rounded-2xl p-5 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 bg-red-100 p-2 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h4 className="text-red-900 font-bold text-lg mb-1">{isRTL ? 'خطأ' : 'Error'}</h4>
              <p className="text-red-700 font-medium">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const complaintsByStatus = [
    { name: isRTL ? 'قيد الانتظار' : 'Pending', value: stats.pendingComplaints, color: STATUS_COLORS.pending },
    { name: isRTL ? 'قيد المعالجة' : 'In Progress', value: stats.inProgressComplaints, color: STATUS_COLORS.inProgress },
    { name: isRTL ? 'إعادة العمل' : 'Rework', value: stats.reworkRequestedComplaints, color: STATUS_COLORS.rework },
    { name: isRTL ? 'محلول' : 'Resolved', value: stats.resolvedComplaints, color: STATUS_COLORS.resolved },
    { name: isRTL ? 'مغلق' : 'Closed', value: stats.closedComplaints, color: STATUS_COLORS.closed },
  ].filter(item => item.value > 0);

  const timelineData = stats.monthly_trends?.months?.map((month: any) => ({
    month: isRTL ? month.month_name_ar : month.month_name,
    complaints: month.total_complaints,
    resolved: month.resolved_complaints,
  })) || [];

  const statCards = [
    {
      title: isRTL ? 'إجمالي البلاغات' : 'Total Complaints',
      value: stats.totalComplaints,
      icon: FileText,
      gradient: 'from-brand-500 to-brand-600',
      iconBg: 'bg-brand-100',
      iconColor: 'text-brand-600',
      change: '+12%',
      trending: 'up',
    },
    {
      title: isRTL ? 'قيد الانتظار' : 'Pending',
      value: stats.pendingComplaints,
      icon: Clock,
      gradient: 'from-amber-500 to-amber-600',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      change: '-5%',
      trending: 'down',
    },
    {
      title: isRTL ? 'محلول' : 'Resolved',
      value: stats.resolvedComplaints + stats.closedComplaints,
      icon: CheckCircle,
      gradient: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      change: '+18%',
      trending: 'up',
    },
    {
      title: isRTL ? 'خروقات SLA' : 'SLA Breaches',
      value: stats.slaBreachCount,
      icon: AlertTriangle,
      gradient: 'from-red-500 to-red-600',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      change: '-8%',
      trending: 'down',
    },
    {
      title: isRTL ? 'إجمالي الشركات' : 'Total Companies',
      value: stats.totalCompanies,
      icon: Building2,
      gradient: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      title: isRTL ? 'الشركات النشطة' : 'Active Companies',
      value: stats.activeCompanies,
      icon: Building2,
      gradient: 'from-cyan-500 to-cyan-600',
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-600',
    },
    {
      title: isRTL ? 'إجمالي المقيمين' : 'Total Evaluators',
      value: stats.totalEvaluators,
      icon: Users,
      gradient: 'from-pink-500 to-pink-600',
      iconBg: 'bg-pink-100',
      iconColor: 'text-pink-600',
    },
    {
      title: isRTL ? 'متوسط وقت الحل' : 'Avg Resolution Time',
      value: `${Math.round(stats.avgResolutionTime)}`,
      unit: isRTL ? 'يوم' : 'days',
      icon: Timer,
      gradient: 'from-indigo-500 to-indigo-600',
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-green-50 to-emerald-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">

        {/* Premium Header with Gradient */}
        <div className="relative overflow-hidden rounded-3xl">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>

          <div className="relative px-6 py-8 md:px-10 md:py-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-white/30 rounded-2xl blur-xl"></div>
                  <div className="relative bg-white/20 backdrop-blur-md p-4 rounded-2xl border border-white/30 shadow-2xl">
                    <Activity className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
                    {isRTL ? 'لوحة التحكم' : 'Dashboard'}
                  </h1>
                  <p className="text-white/80 text-sm md:text-base font-medium mt-1">
                    {isRTL ? 'نظرة عامة على الأداء والإحصائيات' : 'Overview of performance and statistics'}
                  </p>
                </div>
              </div>

              <button
                onClick={fetchStats}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-md text-white rounded-xl
                         hover:bg-white/30 transition-all border border-white/30 font-semibold disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                <span>{isRTL ? 'تحديث' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-gradient-to-r from-red-50 to-rose-50 border-r-4 border-red-500 rounded-2xl p-5 shadow-lg animate-shake">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-red-100 p-2 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h4 className="text-red-900 font-bold text-lg mb-1">{isRTL ? 'خطأ' : 'Error'}</h4>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </div>
              <button
                onClick={() => setError('')}
                className="flex-shrink-0 text-red-400 hover:text-red-600 hover:bg-red-100 p-2 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards Grid - Modern Design */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden
                         hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-5 transition-opacity`}></div>

                <div className="relative p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 ${card.iconBg} rounded-2xl flex items-center justify-center
                                  group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-7 h-7 ${card.iconColor}`} />
                    </div>
                    {card.trending && (
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold
                        ${card.trending === 'up' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {card.trending === 'up' ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        <span>{card.change}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">{card.title}</p>
                    <h3 className="text-3xl font-black text-gray-900 group-hover:text-brand-700 transition-colors">
                      {card.value}
                      {card.unit && <span className="text-lg font-bold text-gray-500 ml-1">{card.unit}</span>}
                    </h3>
                  </div>
                </div>

                {/* Bottom Accent Line */}
                <div className={`h-1 bg-gradient-to-r ${card.gradient}`}></div>
              </div>
            );
          })}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Pie Chart - Complaints by Status */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animate-fadeIn">
            <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-brand-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {isRTL ? 'البلاغات حسب الحالة' : 'Complaints by Status'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isRTL ? 'توزيع البلاغات حسب الحالة الحالية' : 'Distribution by current status'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {complaintsByStatus.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[300px] text-gray-400">
                  <FileText className="w-16 h-16 mb-3" />
                  <p className="text-sm font-medium">{isRTL ? 'لا توجد بيانات' : 'No data available'}</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={complaintsByStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, value, percent }) =>
                        percent > 0.05 ? `${name}: ${value}` : ''
                      }
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {complaintsByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Line Chart - Trend */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animate-fadeIn" style={{ animationDelay: '0.1s' }}>
            <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {isRTL ? 'الاتجاه الشهري' : 'Monthly Trend'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isRTL ? 'آخر 6 أشهر' : 'Last 6 months'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {timelineData.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[300px] text-gray-400">
                  <Activity className="w-16 h-16 mb-3" />
                  <p className="text-sm font-medium">{isRTL ? 'لا توجد بيانات' : 'No trend data'}</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={timelineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" style={{ fontSize: '12px' }} />
                    <YAxis style={{ fontSize: '12px' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="complaints"
                      stroke="#16a34a"
                      name={isRTL ? 'الإجمالي' : 'Total'}
                      strokeWidth={3}
                      dot={{ r: 5, fill: '#16a34a' }}
                      activeDot={{ r: 7 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="resolved"
                      stroke="#10b981"
                      name={isRTL ? 'محلول' : 'Resolved'}
                      strokeWidth={3}
                      dot={{ r: 5, fill: '#10b981' }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Bar Chart - Full Width */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animate-fadeIn" style={{ animationDelay: '0.2s' }}>
          <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-brand-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {isRTL ? 'الأداء الشهري' : 'Monthly Performance'}
                </h3>
                <p className="text-sm text-gray-600">
                  {isRTL ? 'مقارنة إجمالي البلاغات مع المحلولة' : 'Comparison of total vs resolved complaints'}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {timelineData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[350px] text-gray-400">
                <BarChart3 className="w-16 h-16 mb-3" />
                <p className="text-sm font-medium">{isRTL ? 'لا توجد بيانات' : 'No performance data'}</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" style={{ fontSize: '12px' }} />
                  <YAxis style={{ fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="complaints"
                    fill="#16a34a"
                    name={isRTL ? 'إجمالي البلاغات' : 'Total Complaints'}
                    radius={[8, 8, 0, 0]}
                  />
                  <Bar
                    dataKey="resolved"
                    fill="#10b981"
                    name={isRTL ? 'محلول' : 'Resolved'}
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
